import { colors, typography } from '../styles/theme';
import { withAlpha } from '../Map/utils/sharedBranding';
import { FORM_GROUP_TITLE_SX } from '../Map/components/DockPanel/Tabs/formBlockUi';

export const SHOWROOM_HEADER_HEIGHT = 64;
export const SHOWROOM_PANEL_BG = colors.backgroundSurface;
export const SHOWROOM_PANEL_BORDER = colors.border;

export const showroomHeaderBarSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: SHOWROOM_HEADER_HEIGHT,
  boxSizing: 'border-box',
  zIndex: 1100,
  bgcolor: colors.backgroundSurface,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  px: 2,
};

export const showroomPrimaryButtonSx = {
  textTransform: 'none',
  px: 2,
  fontSize: '13px',
  fontWeight: 600,
  borderRadius: 1.5,
  bgcolor: colors.primary,
  color: '#ffffff',
  boxShadow: 'none',
  fontFamily: typography.fontFamily.primary,
  '&:hover': {
    bgcolor: colors.primaryHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'rgba(72,146,145,0.35)',
    color: 'rgba(255,255,255,0.55)',
  },
};

export const showroomOutlinedButtonSx = {
  ...showroomPrimaryButtonSx,
  bgcolor: 'transparent',
  color: '#ffffff',
  borderColor: 'rgba(255,255,255,0.22)',
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.06)',
    borderColor: 'rgba(255,255,255,0.30)',
  },
  '&.Mui-disabled': {
    color: colors.textSecondary,
    borderColor: colors.border,
    bgcolor: 'transparent',
    opacity: 1,
  },
};

export const showroomPageRootSx = {
  height: '100vh',
  width: '100%',
  position: 'relative',
  bgcolor: '#0f0f0f',
};

export const showroomPaperSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: '100%',
  minHeight: '100%',
  bgcolor: SHOWROOM_PANEL_BG,
  backgroundImage: 'none',
  color: colors.textPrimary,
  boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
  border: `1px solid ${SHOWROOM_PANEL_BORDER}`,
  borderRadius: { xs: 0, sm: '16px' },
};

/**
 * Paper del panel CAD con acento de marca (misma barra de 3px que DockPanel branded).
 * @param {string|null|undefined} accentColor
 */
export function showroomBrandedPaperSx(accentColor) {
  if (!accentColor) return showroomPaperSx;
  return {
    ...showroomPaperSx,
    border: 'none',
  };
}

/**
 * Botón primario con el color de marca (consulta del visitante branded).
 * La vista Terrio sigue usando `FORM_PRIMARY_BUTTON_SX` en el form.
 * @param {string} accentColor
 */
export function showroomBrandedPrimaryButtonSx(accentColor) {
  return {
    textTransform: 'none',
    borderRadius: '8px',
    minHeight: 40,
    height: 40,
    px: 2.25,
    fontSize: '14px',
    fontWeight: typography.fontWeight.semibold,
    fontFamily: typography.fontFamily.primary,
    color: colors.textPanelDescription,
    bgcolor: accentColor,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: accentColor,
      filter: 'brightness(0.92)',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      bgcolor: withAlpha(accentColor, 0.35),
      color: withAlpha(colors.textPanelDescription, 0.55),
      filter: 'none',
    },
  };
}

/**
 * CTA del header de ficha: baja al formulario de consulta.
 * @param {string|null|undefined} accentColor
 */
export function showroomInquiryHeaderButtonSx(accentColor) {
  const fill = accentColor || colors.primary;
  return {
    textTransform: 'none',
    minWidth: 0,
    height: 32,
    minHeight: 32,
    px: 1.25,
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: typography.fontWeight.semibold,
    fontFamily: typography.fontFamily.primary,
    color: colors.textPanelDescription,
    bgcolor: fill,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: fill,
      filter: 'brightness(0.92)',
      boxShadow: 'none',
    },
  };
}

export const showroomScrollSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  px: 2.5,
  pb: 2.5,
  minHeight: 0,
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.22) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  '&::-webkit-scrollbar-corner': { background: 'transparent' },
};

export const showroomToolbarSx = {
  flexShrink: 0,
  px: 2.5,
  pt: 1.5,
  pb: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  bgcolor: SHOWROOM_PANEL_BG,
  borderBottom: `1px solid ${SHOWROOM_PANEL_BORDER}`,
};

export const showroomCountsRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  width: '100%',
  minHeight: 24,
};

/** Trigger de menú compacto (Estado / Ordenar), mismo chrome que Registros. */
export const showroomCompactMenuButtonSx = {
  textTransform: 'none',
  color: colors.textSecondary,
  fontSize: '12px',
  fontWeight: 400,
  minHeight: 24,
  px: 0.5,
  fontFamily: typography.fontFamily.primary,
  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: colors.textPanelTitle },
  '& .MuiButton-endIcon': { ml: 0.25 },
};

export const showroomFieldSx = {
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.72)',
    fontFamily: typography.fontFamily.primary,
  },
  '& .MuiInputLabel-root.Mui-focused': { color: colors.textPrimary },
  '& .MuiInputLabel-root.Mui-disabled': { color: 'rgba(255,255,255,0.72)' },
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.backgroundDark,
    borderRadius: '8px',
    color: colors.textPrimary,
    fontFamily: typography.fontFamily.primary,
    fontSize: '14px',
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
    '&.Mui-focused fieldset': { borderColor: colors.primary },
    '&.Mui-disabled': {
      backgroundColor: colors.backgroundDark,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: colors.border },
    },
  },
  '& .MuiOutlinedInput-input': {
    color: colors.textPrimary,
  },
  '& .MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: colors.textSecondary,
    color: colors.textSecondary,
  },
};

/**
 * Inputs del form CAD con foco en el color de marca.
 * @param {string|null|undefined} accentColor
 */
export function showroomBrandedFieldSx(accentColor) {
  if (!accentColor) return showroomFieldSx;
  return {
    ...showroomFieldSx,
    '& .MuiInputLabel-root.Mui-focused': { color: accentColor },
    '& .MuiOutlinedInput-root': {
      ...showroomFieldSx['& .MuiOutlinedInput-root'],
      '&.Mui-focused fieldset': { borderColor: accentColor },
    },
  };
}

export const showroomSelectSx = {
  backgroundColor: colors.backgroundDark,
  borderRadius: '8px',
  color: colors.textPrimary,
  fontSize: '14px',
  fontFamily: typography.fontFamily.primary,
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.65)' },
  '& .MuiSelect-select': { color: colors.textPrimary },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
  '&.Mui-disabled': {
    color: colors.textSecondary,
    '& .MuiSelect-icon': { color: colors.textSecondary },
    '& .MuiSelect-select': { WebkitTextFillColor: colors.textSecondary, color: colors.textSecondary },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
  },
};

export const showroomMenuItemSx = {
  color: colors.textPrimary,
  fontSize: '13px',
  fontFamily: typography.fontFamily.primary,
  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
  '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
  '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
};

export const showroomFormLabelSx = {
  color: 'rgba(255,255,255,0.72)',
  fontFamily: typography.fontFamily.primary,
  '&.Mui-focused': { color: colors.textPrimary },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.72)' },
};

export const showroomFormFooterSx = {
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 1,
  px: 2.5,
  py: 1.5,
  borderTop: `1px solid ${colors.border}`,
  bgcolor: 'rgba(0,0,0,0.18)',
};

export const showroomFormGroupDividerSx = {
  borderTop: `1px solid ${colors.border}`,
  pt: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

/** Título de grupo dentro de `showroomFormGroupDividerSx` (el gap del padre reemplaza el mb). */
export const showroomFormGroupTitleSx = {
  ...FORM_GROUP_TITLE_SX,
  mb: 0,
};

export const showroomMenuPaperSx = {
  bgcolor: colors.backgroundSurface,
  backgroundImage: 'none',
  border: `1px solid ${colors.border}`,
  mt: 0.5,
};

export const showroomCardSx = {
  px: 1.5,
  py: 1.25,
  borderRadius: '8px',
  border: 'none',
  bgcolor: colors.backgroundDark,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  transition: 'background-color 0.15s ease',
  '&:hover': {
    bgcolor: '#2a2a2a',
  },
};
